<div class="min-vh-100 bg-light">
  <app-header [page]="'Account Security'"></app-header>

  <main class="container py-4" style="max-width: 980px;">
    <div class="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-4">
      <div>
        <h4 class="fw-bold mb-1">Account Security</h4>
        <div class="text-muted">Manage your password, account PIN, login method, and transaction approvals.</div>
      </div>
      <a routerLink="/account" class="btn btn-outline-secondary btn-sm">Back to account</a>
    </div>

    <div class="alert alert-danger" *ngIf="error">{{ error }}</div>
    <div class="text-center py-5" *ngIf="loading"><div class="spinner-border text-primary"></div></div>

    <ng-container *ngIf="!loading">
      <div class="card border-0 shadow-sm mb-4">
        <div class="card-body p-4 d-flex flex-wrap justify-content-between align-items-center gap-3">
          <div>
            <div class="fw-bold">Password</div>
            <div class="text-muted small">Change your password and revoke older sessions.</div>
          </div>
          <a routerLink="/auth/change-password" class="btn btn-outline-primary">Change password</a>
        </div>
      </div>

      <div class="card border-0 shadow-sm mb-4" *ngIf="!settings?.isSet">
        <div class="card-body p-4">
          <h5 class="fw-bold">Set up account PIN</h5>
          <p class="text-muted">Create a six-digit PIN. It is separate from the registration verification code.</p>

          <form [formGroup]="setupForm" (ngSubmit)="setup()">
            <div class="row g-3">
              <div class="col-md-4">
                <label class="form-label">Current password</label>
                <input type="password" class="form-control" formControlName="currentPassword" autocomplete="current-password" />
              </div>
              <div class="col-md-4">
                <label class="form-label">New six-digit PIN</label>
                <input type="password" class="form-control" formControlName="pin" inputmode="numeric" maxlength="6" />
              </div>
              <div class="col-md-4">
                <label class="form-label">Confirm PIN</label>
                <input type="password" class="form-control" formControlName="confirmPin" inputmode="numeric" maxlength="6" />
              </div>
            </div>

            <div class="row g-3 mt-1">
              <div class="col-md-4"><div class="form-check form-switch"><input class="form-check-input" type="checkbox" formControlName="allowPinLogin"><label class="form-check-label">Allow PIN login</label></div></div>
              <div class="col-md-4"><div class="form-check form-switch"><input class="form-check-input" type="checkbox" formControlName="requirePinForDeposits"><label class="form-check-label">Require PIN for deposits</label></div></div>
              <div class="col-md-4"><div class="form-check form-switch"><input class="form-check-input" type="checkbox" formControlName="requirePinForWithdrawals"><label class="form-check-label">Require PIN for withdrawals</label></div></div>
            </div>

            <button class="btn btn-primary mt-4" type="submit" [disabled]="saving || setupForm.invalid">Set up PIN</button>
          </form>
        </div>
      </div>

      <ng-container *ngIf="settings?.isSet">
        <div class="card border-0 shadow-sm mb-4">
          <div class="card-body p-4">
            <div class="d-flex justify-content-between align-items-start gap-3 mb-3">
              <div><h5 class="fw-bold mb-1">PIN preferences</h5><div class="text-muted small">Enter your current PIN to save changes.</div></div>
              <span class="badge text-bg-success">PIN enabled</span>
            </div>

            <form [formGroup]="settingsForm" (ngSubmit)="saveSettings()">
              <div class="row g-3 align-items-end">
                <div class="col-md-4"><div class="form-check form-switch"><input class="form-check-input" type="checkbox" formControlName="allowPinLogin"><label class="form-check-label">Allow PIN login</label></div></div>
                <div class="col-md-4"><div class="form-check form-switch"><input class="form-check-input" type="checkbox" formControlName="requirePinForDeposits"><label class="form-check-label">Require PIN for deposits</label></div></div>
                <div class="col-md-4"><div class="form-check form-switch"><input class="form-check-input" type="checkbox" formControlName="requirePinForWithdrawals"><label class="form-check-label">Require PIN for withdrawals</label></div></div>
                <div class="col-md-5"><label class="form-label">Current PIN</label><input type="password" class="form-control" formControlName="pin" maxlength="6" inputmode="numeric"></div>
                <div class="col-md-3"><button class="btn btn-primary w-100" type="submit" [disabled]="saving || settingsForm.invalid">Save preferences</button></div>
              </div>
            </form>
          </div>
        </div>

        <div class="row g-4">
          <div class="col-lg-6">
            <div class="card border-0 shadow-sm h-100"><div class="card-body p-4">
              <h5 class="fw-bold">Change PIN</h5>
              <form [formGroup]="changeForm" (ngSubmit)="changePin()">
                <label class="form-label">Current PIN</label><input type="password" class="form-control mb-3" formControlName="currentPin" maxlength="6" inputmode="numeric">
                <label class="form-label">New PIN</label><input type="password" class="form-control mb-3" formControlName="newPin" maxlength="6" inputmode="numeric">
                <label class="form-label">Confirm new PIN</label><input type="password" class="form-control mb-3" formControlName="confirmNewPin" maxlength="6" inputmode="numeric">
                <button class="btn btn-outline-primary" type="submit" [disabled]="saving || changeForm.invalid">Change PIN</button>
              </form>
            </div></div>
          </div>

          <div class="col-lg-6">
            <div class="card border-0 shadow-sm h-100"><div class="card-body p-4">
              <h5 class="fw-bold">Forgot PIN</h5>
              <p class="text-muted small">Reset with your password<span *ngIf="otpEnabled"> and authenticator code</span>. All PIN uses will be switched off after reset.</p>
              <form [formGroup]="resetForm" (ngSubmit)="resetPin()">
                <label class="form-label">Current password</label><input type="password" class="form-control mb-3" formControlName="currentPassword">
                <label class="form-label">New PIN</label><input type="password" class="form-control mb-3" formControlName="newPin" maxlength="6" inputmode="numeric">
                <label class="form-label">Confirm new PIN</label><input type="password" class="form-control mb-3" formControlName="confirmNewPin" maxlength="6" inputmode="numeric">
                <div *ngIf="otpEnabled"><label class="form-label">Authenticator code</label><input type="text" class="form-control mb-3" formControlName="totpCode" maxlength="6" inputmode="numeric"></div>
                <button class="btn btn-outline-warning" type="submit" [disabled]="saving || resetForm.invalid">Reset PIN</button>
              </form>
            </div></div>
          </div>
        </div>

        <div class="card border-danger shadow-sm mt-4">
          <div class="card-body p-4">
            <h5 class="fw-bold text-danger">Disable account PIN</h5>
            <p class="text-muted small">This also disables PIN login and transaction PIN protection.</p>
            <form class="row g-3 align-items-end" [formGroup]="disableForm" (ngSubmit)="disablePin()">
              <div class="col-md-5"><label class="form-label">Current PIN</label><input type="password" class="form-control" formControlName="pin" maxlength="6" inputmode="numeric"></div>
              <div class="col-md-3"><button class="btn btn-danger w-100" type="submit" [disabled]="saving || disableForm.invalid">Disable PIN</button></div>
            </form>
          </div>
        </div>
      </ng-container>
    </ng-container>
  </main>
</div>
